import React, { useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BlockIcon from "@mui/icons-material/Block";
import DeleteIcon from "@mui/icons-material/Delete";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PublishIcon from "@mui/icons-material/Publish";
import { Alert, CircularProgress, Divider } from '@mui/material';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import PdfService from "../../../pages/puzzles/common/PdfService";
import Logger from "../../../services/Logger";
import SocialShareDropdown from "../../../pages/puzzles/common/components/SocialShareDropdown";
import PreviewImageDialog, {
    getPreviewPuzzleInstanceId,
    resolvePreviewImageUrl,
} from "./PreviewImageDialog";

export default function StepFooter({
    stepType,
    title,
    description,
    descriptionExtra,
    onDiscard,
    showDiscard = true,
    // Button handlers
    onNextStep,
    onReview,
    onGoBackDraft,
    onGoBackReadyToReview,
    onPublish,
    onDisable,
    onApplyTitleHook,
    // Loading states
    loadingReview = false,
    loadingGoBackDraft = false,
    loadingGoBackReadyToReview = false,
    loadingPublish = false,
    loadingDiscard = false,
    loadingNextStep = false,
    loadingDisable = false,
    loadingApplyTitleHook = false,
    // Puzzle data for PDF and Share buttons
    puzzle,
    onError,
    onSuccess,
    // Free checkbox (published step only)
    free = false,
    onFreeClick,
    loadingFree = false,
    /** Extra controls after the share button (e.g. grid buggle chart) */
    afterShare = null,
}) {
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [pdfMenuAnchor, setPdfMenuAnchor] = useState(null);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

    const previewPuzzleId = getPreviewPuzzleInstanceId(puzzle);
    const previewImageUrl = resolvePreviewImageUrl(puzzle);
    const showPreviewImageButton =
        puzzle?.type === "CROSSWORD" ||
        puzzle?.type === "WORD_SEARCH" ||
        puzzle?.type === "HIDDEN_MESSAGE";

    const doDownloadPdf = async (format, useDisplayClue = false) => {
        try {
            setDownloadingPdf(true);
            setPdfMenuAnchor(null);
            const actualPuzzleType = puzzle?.type === 'CROSSWORD'
                ? 'CROSSWORD'
                : (puzzle?.type || 'WORD_SEARCH');
            await PdfService.downloadPuzzle(puzzle?.id, actualPuzzleType, format, useDisplayClue);
            if (onSuccess) {
                onSuccess("PDF downloaded successfully", "success");
            }
        } catch (error) {
            Logger.error("Error downloading PDF:", error);
            if (onError) {
                onError(error?.message || "Failed to download PDF. Please try again.");
            }
        } finally {
            setDownloadingPdf(false);
        }
    };

    const handlePdfClick = (event) => {
        setPdfMenuAnchor(event.currentTarget);
    };

    const handlePdfMenuClose = () => {
        setPdfMenuAnchor(null);
    };

    const handlePdfFormatSelect = (format, useDisplayClue = false) => {
        doDownloadPdf(format, useDisplayClue);
    };

    // Show PDF and Share buttons only for reviewed or published puzzles
    const showPdfAndShare = (stepType === 'reviewed' || stepType === 'published') && puzzle?.id;
    // Define step-specific styling
    const getStepStyle = () => {
        switch (stepType) {
            case 'draft':
                return {
                    backgroundColor: "#f8f9fa",
                    borderColor: "#dee2e6"
                };
            case 'ready-for-review':
                return {
                    backgroundColor: "#fff3e0",
                    borderColor: "#ffcc02"
                };
            case 'reviewed':
                return {
                    backgroundColor: "#e3f2fd",
                    borderColor: "#2196f3"
                };
            case 'published':
                return {
                    backgroundColor: "#e8f5e8",
                    borderColor: "#4caf50"
                };

            default:
                return {
                    backgroundColor: "#f8f9fa",
                    borderColor: "#dee2e6"
                };
        }
    };

    // Define step-specific buttons
    const getStepButtons = () => {
        switch (stepType) {
            case 'draft':
                return (
                    <>
                        <Button
                            variant="contained"
                            endIcon={loadingNextStep ? <CircularProgress size={16} color="inherit" /> : <NavigateNextIcon />}
                            onClick={onNextStep}
                            disabled={loadingNextStep}
                        >
                            Ready to Review
                        </Button>
                    </>
                );
            case 'ready-for-review':
                return (
                    <>
                        {onApplyTitleHook && (
                            <Tooltip title="Change the title using the Bedrock">
                                <Button
                                    variant="outlined"
                                    startIcon={loadingApplyTitleHook ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                                    onClick={onApplyTitleHook}
                                    disabled={loadingApplyTitleHook || loadingGoBackDraft || loadingReview}
                                    sx={{
                                        color: '#9c27b0',
                                        borderColor: '#9c27b0',
                                        '&:hover': {
                                            backgroundColor: '#9c27b0',
                                            color: 'white',
                                            borderColor: '#9c27b0'
                                        }
                                    }}
                                >
                                    Hook The Title
                                </Button>
                            </Tooltip>
                        )}
                        <Button
                            variant="outlined"
                            color="warning"
                            startIcon={loadingGoBackDraft ? <CircularProgress size={16} color="inherit" /> : <ArrowBackIcon />}
                            onClick={onGoBackDraft}
                            disabled={loadingGoBackDraft || loadingReview || loadingApplyTitleHook}
                        >
                            Draft
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            endIcon={loadingReview ? <CircularProgress size={16} color="inherit" /> : <NavigateNextIcon />}
                            onClick={onReview}
                            disabled={loadingReview || loadingGoBackDraft || loadingApplyTitleHook}
                        >
                            Review
                        </Button>
                    </>
                );
            case 'reviewed':
                return (
                    <>
                        <Button
                            variant="outlined"
                            color="warning"
                            startIcon={loadingGoBackReadyToReview ? <CircularProgress size={16} color="inherit" /> : <ArrowBackIcon />}
                            onClick={onGoBackReadyToReview}
                            disabled={loadingGoBackReadyToReview || loadingPublish}
                        >
                            Back to Ready-to-Review
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={loadingPublish ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />}
                            onClick={onPublish}
                            disabled={loadingPublish || loadingGoBackReadyToReview}
                        >
                            Publish
                        </Button>
                    </>
                );
            case 'published':
                return (
                    <>
                        <Button
                            variant="outlined"
                            color="warning"
                            startIcon={loadingReview ? <CircularProgress size={16} color="inherit" /> : <ArrowBackIcon />}
                            onClick={onReview}
                            disabled={loadingReview || loadingDisable}
                        >
                            Back to Review
                        </Button>
                        {onDisable && (
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={loadingDisable ? <CircularProgress size={16} color="inherit" /> : <BlockIcon />}
                                onClick={onDisable}
                                disabled={loadingDisable || loadingReview || puzzle?.state === 'DISABLED'}
                            >
                                Disable
                            </Button>
                        )}
                    </>
                );

            default:
                return null;
        }
    };

    const stepStyle = getStepStyle();
    const stepButtons = getStepButtons();

    return (
        <Box sx={{
            display: "flex",
            flexDirection: "column",
            mt: 4,
            p: 3,
            backgroundColor: stepStyle.backgroundColor,
            borderRadius: 2,
            border: `1px solid ${stepStyle.borderColor}`,
            position: "relative"
        }}>
            {/* Top section with title and action buttons */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
                    <Typography variant="h6" gutterBottom>
                        {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {description}
                    </Typography>
                    {stepType === 'reviewed' && (
                        <Alert severity="info" sx={{ mt: 1, py: 0.5, fontSize: '0.75rem' }}>
                            Reviewed puzzles are automatically published around 10 a.m. on Monday and Thursday.
                            You can publish this puzzle manually now.
                        </Alert>
                    )}
                </Box>

                {/* Free, PDF and Share - top-right, same row as "Published & Live" / title */}
                {((stepType === 'published' && onFreeClick) || showPdfAndShare) && (
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", ml: 2 }}>
                        {/* Published only: Free checkbox first */}
                        {stepType === 'published' && onFreeClick && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!free}
                                        disabled={loadingFree}
                                        onChange={(e) => onFreeClick(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={loadingFree ? "Updating…" : "Free"}
                            />
                        )}
                        {showPreviewImageButton ? (
                            <>
                                {previewImageUrl || previewPuzzleId ? (
                                    <Tooltip title="Preview image" arrow>
                                        <IconButton
                                            onClick={() => setPreviewDialogOpen(true)}
                                            sx={{
                                                backgroundColor: "rgba(255, 255, 255, 0.9)",
                                                borderRadius: "50%",
                                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                                flexShrink: 0,
                                                "&:hover": {
                                                    backgroundColor: "rgba(255, 255, 255, 1)"
                                                }
                                            }}
                                        >
                                            <ImageIcon />
                                        </IconButton>
                                    </Tooltip>
                                ) : null}
                                <Tooltip title="Download as PDF" arrow>
                                    <IconButton
                                        onClick={handlePdfClick}
                                        disabled={downloadingPdf || !puzzle?.id}
                                        sx={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            borderRadius: '50%',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                            flexShrink: 0,
                                            cursor: downloadingPdf ? 'wait' : 'pointer',
                                            '&.Mui-disabled': { opacity: 0.7 }
                                        }}
                                    >
                                        {downloadingPdf ? <CircularProgress size={24} /> : <PictureAsPdfIcon />}
                                    </IconButton>
                                </Tooltip>
                                <Menu
                                    anchorEl={pdfMenuAnchor}
                                    open={Boolean(pdfMenuAnchor)}
                                    onClose={handlePdfMenuClose}
                                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                                >
                                    <MenuItem
                                        onClick={() => handlePdfFormatSelect("letter", false)}
                                        disabled={downloadingPdf}
                                    >
                                        Dictionary - Letter
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => handlePdfFormatSelect("a4", false)}
                                        disabled={downloadingPdf}
                                    >
                                        Dictionary A4
                                    </MenuItem>
                                    <Divider component="li" sx={{ borderStyle: "dashed" }} />
                                    <MenuItem
                                        onClick={() => handlePdfFormatSelect("letter", true)}
                                        disabled={downloadingPdf}
                                    >
                                        Display Clue — Letter
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => handlePdfFormatSelect("a4", true)}
                                        disabled={downloadingPdf}
                                    >
                                        Display Clue — A4
                                    </MenuItem>
                                </Menu>
                            </>
                        ) : null}
                        {puzzle?.title && (
                            <SocialShareDropdown
                                puzzleTitle={puzzle.title}
                                puzzleId={puzzle.type === 'CROSSWORD' ? (puzzle.crossword?.id) : (puzzle.grid?.id)}
                                puzzleType={puzzle.type || 'WORD_SEARCH'}
                                onCopyLink={() => onSuccess && onSuccess("Link copied to clipboard", "success")}
                            />
                        )}
                        {afterShare}
                    </Box>
                )}
            </Box>

            {/* Bottom section with action buttons */}
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap" }}>
                {stepType === "draft" && descriptionExtra}
                {stepButtons}

                {/* Discard button - shown on all steps */}
                {showDiscard && (
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={loadingDiscard ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                        onClick={onDiscard}
                        disabled={loadingDiscard || loadingReview || loadingGoBackDraft || loadingGoBackReadyToReview || loadingPublish || loadingNextStep || loadingDisable || loadingApplyTitleHook}
                        sx={{
                            px: 2,
                            py: 1
                        }}
                    >
                        Discard
                    </Button>
                )}
            </Box>

            <PreviewImageDialog
                open={previewDialogOpen}
                onClose={() => setPreviewDialogOpen(false)}
                imageUrl={previewImageUrl}
                puzzleType={puzzle?.type}
                puzzleId={previewPuzzleId}
                onError={onError}
            />

        </Box>
    );
}

